import {NumberOutlined, PercentageOutlined, PlayCircleOutlined} from '@ant-design/icons';
import { Hash, Percent, DollarSign, Tag as TagIcon } from 'lucide-react';
import {Tag} from 'antd';

// Type to color/icon mapping
const METRIC_TYPES = {
    number: {
        gradient: 'linear-gradient(#8845FF, #5D02FC)',
        Icon: Hash
    },
    currency: {
        gradient: 'linear-gradient(#53F9E3, #00B59C)',
        Icon: DollarSign
    },
    percentage: {
        gradient: 'linear-gradient(#5395F9, #5395F9)',
        Icon: Percent
    },
    categorical: {
        gradient: 'linear-gradient(#6366f1, #4F46E5)',
        Icon: TagIcon
    }
};

// Updated color handler to use METRIC_TYPES
const _handleGetScreenTypeColor = (type) => METRIC_TYPES[type]?.gradient;

// Updated getter to handle both metric and categorical types
export const getScreenTypeColor = (name) => {
    if (name.startsWith('tags.')) {
        return METRIC_TYPES.categorical.gradient;
    }
    return _handleGetScreenTypeColor(screenerMetrics[name]?.type);
};

// Updated icon component to handle all types
export const ScreenerTypeIcon = ({type}) => {
    const IconComponent = METRIC_TYPES[type]?.Icon || TagIcon;
    const color = _handleGetScreenTypeColor(type);
    return (
        <IconComponent size={22} className={'px-1'} style={{color}} />
    );
};

// Rest of your existing exports...
export const BASE_PROJECTION_FIELDS = [
    'symbol', 'name', 'tags.sector', 'tags.industry', 'tags.category', 'tags.issuer',
    'tags.size', 'tags.style', 'metrics.dy', 'metrics.pe_ttm', 'logo', 'metrics.mktcap',
    'exchange', 'stats.benchmarks.spy.corr_1_yr', 'stats.benchmarks.spy.upside', 'stats.benchmarks.spy.downside',
];

export const screenerMetrics = {
    book_value: { type: "number", min: 0, max: 1000, step: 10  },
    dy: { type: "percentage", min: 0, max: 10, step: 0.1},
    ebitda: { type: "currency", min: -100000000, max: 1000000000 },
    evr: { type: "number", min: 0, max: 50 },
    mktcap: { type: "currency", min: 0, max: 1000000000000 },
    operating_margin_ttm: { type: "percentage", min: -100, max: 100 },
    pe_fwd: { type: "number",  min: 0, max: 100 },
    pe_ttm: { type: "number",  min: 0, max: 100 },
    peg: { type: "number",  min: 0, max: 10 },
    price_book: { type: "number",  min: 0, max: 50 },
    price_sales: { type: "number",  min: 0, max: 50 },
    profit_margin: { type: "percentage",  min: -100, max: 100 },
    roa_ttm: { type: "percentage",  min: -100, max: 100 },
    short_ratio: { type: "number",  min: 0, max: 50 },
    short_percent: { type: "percentage",  min: 0, max: 100 },
    analyst_target: { type: "currency" },
    analyst_rating: { type: "number" }
};

export const categoricalFilters = {
    stock: {
        sector: { type: "categorical", label: "Sector" },
        industry: { type: "categorical", label: "Industry" }
    },
    etf: {
        category: { type: "categorical", label: "Category" },
        issuer: { type: "categorical", label: "Issuer" },
        size: { type: "categorical", label: "Size" },
        style: { type: "categorical", label: "Style" }
    }
};

export const screenerFieldToName = (field) => {
    const metricNames = {
        book_value: "Book Value",
        dy: "Divd Yield",
        ebitda: "EBITDA",
        evr: "EV/Revenue",
        mktcap: "Market Cap",
        operating_margin_ttm: "Op. Margin (TTM)",
        pe_fwd: "P/E (Fwd)",
        pe_ttm: "P/E (TTM)",
        peg: "PEG",
        price_book: "Price/Book",
        price_sales: "Price/Sales",
        profit_margin: "Profit Margin",
        roa_ttm: "ROA (TTM)",
        short_ratio: "Short Ratio",
        short_percent: "Short Percent",
        analyst_target: "Analyst Target",
        analyst_rating: "Analyst Rating"
    };

    // Handle both metric and categorical fields
    if (field.startsWith('tags.')) {
        const key = field.replace('tags.', '');
        // Look through both stock and etf filters to find the label
        const allFilters = {...categoricalFilters.stock, ...categoricalFilters.etf};
        return allFilters[key]?.label || key;
    }

    return metricNames[field];
};